/**
 * WT1 - the report screen, `/tour/:tourId/report` (plan section E; spec section 7).
 *
 * The report in the Apex look, SCALED to the screen (landscape letter pages on a phone), Print / PDF (the browser's
 * print of the same page - the brochure path), the filing box OFF (LP1.5: he ticks it, and the answer names where
 * it went), and the tour's photos with "Add to property photos", one or several.
 *
 * ⛔ Promotion is the ONE door from a tour into the gallery: nothing here publishes a photo he did not choose, and a
 *   refusal keeps its reason beside its photo.
 * ⭐ The frame is sandboxed `allow-same-origin` and nothing else: no script inside the report ever runs, and the page
 *   can still read the report's size to scale it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { addWayBack } from '../utils/printWindow';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui';
import { api } from '../lib/api';
import { tagLabel } from '../lib/tourTags';
import { displayDate } from '../utils/dates';
import type { PromoteResult, TourDetail, TourReportResponse } from '../types/tours';

const H1 = 'font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]';
const H2 = 'text-[12px] font-semibold uppercase tracking-[.06em] text-gray-500';
const DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
/** Landscape letter at 96 px to the inch - the report's page, until the frame has measured the real one. */
const LETTER_LANDSCAPE = { width: 1056, height: 816 };

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * The report as a file - what Print / PDF does when the print window is blocked. The object url is revoked after
 * 40 s rather than at once, as FileSaver.js does: a url revoked in the same tick can cancel the download.
 */
/**
 * Every image in a document, once each has loaded or failed - read from each image's OWN events.
 * ⛔ Never `document.readyState`: measured in Chrome (the 104th), a window written with document.open / write / close
 *   reads 'complete' the moment close() returns, while the photos just written are still loading - a check made then
 *   saw all 15 of a report's images blank.
 */
const imagesSettled = (doc: Document): Promise<HTMLImageElement[]> =>
  Promise.all(
    Array.from(doc.images).map((img) =>
      img.complete
        ? img
        : new Promise<HTMLImageElement>((resolve) => {
            img.addEventListener('load', () => resolve(img), { once: true });
            img.addEventListener('error', () => resolve(img), { once: true });
          }),
    ),
  );

const downloadHtml = (html: string, filename: string) => {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 40_000);
};

/** The report at its own width, scaled down to fit - so a landscape page reads on a phone. */
function ScaledReport({ html }: { html: string }) {
  const wrap = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const [page, setPage] = useState(LETTER_LANDSCAPE);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const measureReport = () => {
    const root = frame.current?.contentDocument?.documentElement;
    if (root) setPage({ width: Math.max(root.scrollWidth, 1), height: Math.max(root.scrollHeight, 1) });
  };

  const scale = width ? Math.min(1, width / page.width) : 1;
  return (
    <div ref={wrap} className="w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100" style={{ height: page.height * scale }}>
      <iframe
        ref={frame}
        title="Walk-through report"
        srcDoc={html}
        sandbox="allow-same-origin"
        onLoad={measureReport}
        style={{ width: page.width, height: page.height, border: 0, transform: `scale(${scale})`, transformOrigin: 'top left' }}
      />
    </div>
  );
}

export default function TourReport() {
  const { tourId = '' } = useParams();
  const [report, setReport] = useState<TourReportResponse | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [tour, setTour] = useState<TourDetail | null>(null);
  const [tourError, setTourError] = useState<string | null>(null);

  const [printing, setPrinting] = useState(false);
  const [printMessage, setPrintMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const [fileIt, setFileIt] = useState(false);
  const [filing, setFiling] = useState(false);
  const [fileMessage, setFileMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [promoting, setPromoting] = useState(false);
  const [results, setResults] = useState<Record<string, PromoteResult>>({});
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const [coverBusy, setCoverBusy] = useState(false);
  const [coverRefusal, setCoverRefusal] = useState<{ photoId: string; reason: string } | null>(null);

  /** False once the screen has gone - an answer that arrives after that writes nothing. */
  const live = useRef(true);
  /**
   * Each load's number. ⛔ An answer applies only if no later load of its kind has started: a slow first load must
   * not land after the cover's rebuild and put the old cover back, nor one walk-through's answer on another's screen.
   */
  const tourSeq = useRef(0);
  const reportSeq = useRef(0);

  const loadTour = useCallback(async () => {
    const seq = ++tourSeq.current;
    const current = () => live.current && seq === tourSeq.current;
    try {
      const r = await api.getTour(tourId);
      if (!current()) return;
      setTour(r.tour);
      setTourError(null);
    } catch (err) {
      if (current()) setTourError(messageOf(err, 'The walk-through could not be loaded.'));
    }
  }, [tourId]);

  const loadReport = useCallback(async () => {
    const seq = ++reportSeq.current;
    const current = () => live.current && seq === reportSeq.current;
    try {
      const r = await api.getTourReport(tourId);
      if (!current()) return;
      setReport(r);
      setReportError(null);
    } catch (err) {
      if (current()) setReportError(messageOf(err, 'The report could not be built.'));
    }
  }, [tourId]);

  /**
   * ⭐ "Try again" on either failure: the installed app has no reload button, so a refusal with no way to ask
   *   again is a dead end on the phone.
   */
  const retryReport = () => {
    setReportError(null);
    void loadReport();
  };
  const retryTour = () => {
    setTourError(null);
    void loadTour();
  };

  useEffect(() => {
    live.current = true;
    void loadReport();
    void loadTour();
    return () => {
      live.current = false;
    };
  }, [loadReport, loadTour]);

  const where = tour?.property ? [tour.property.address, tour.property.city].filter(Boolean).join(', ') : '';
  const missing = report ? Math.max(0, report.captured - report.uploaded) : 0;

  /**
   * I5 - the brochure's print path (the same page in a window of its own), made to work from a phone:
   *  1. The window opens FIRST, inside the tap: Safari blocks a window opened after anything was awaited.
   *  2. THEN the report is built afresh - its photo links are signed for an hour, and this screen may be older.
   *  3. It prints once every photo has loaded or failed (imagesSettled), and never with one that did not load: that
   *     is a blank box on paper.
   * ⭐ A blocked window is not a dead end: the report is downloaded instead, and says its photos last an hour.
   */
  const print = async () => {
    setPrintMessage(null);
    const w = window.open('', '_blank');
    if (w) {
      w.document.write('<p style="font:15px system-ui,sans-serif;padding:24px;color:#333">Preparing the report</p>');
      w.document.close();
      // MOB1 (the 126th): the waiting page carries the way back too - a build that never answers must not strand him.
      addWayBack(w.document);
    }
    setPrinting(true);
    const fresh = await api.getTourReport(tourId).catch((err: unknown) => {
      w?.close();
      if (live.current) setPrintMessage({ ok: false, text: messageOf(err, 'The report could not be built - try again.') });
      return null;
    });
    if (live.current) setPrinting(false);
    if (!fresh) return;
    if (!w) {
      downloadHtml(fresh.html, fresh.filename);
      if (live.current) {
        setPrintMessage({
          ok: true,
          text:
            'The print window was blocked, so the report was downloaded instead - open it and print it from there. ' +
            'Its photos load for an hour; after that, press Print / PDF again.',
        });
      }
      return;
    }
    // He closed the window while the report was being built: that is an answer, not an error.
    if (w.closed) return;
    w.document.open();
    w.document.write(fresh.html);
    w.document.close();
    // The report replaced the waiting page, bar and all - so the way back is added again (never printed).
    addWayBack(w.document);
    const images = await imagesSettled(w.document);
    const blank = images.filter((img) => img.naturalWidth === 0).length;
    if (blank) {
      w.close();
      if (live.current) {
        setPrintMessage({
          ok: false,
          text: `${blank === 1 ? '1 photo' : `${blank} photos`} did not load, so nothing was printed - press Print / PDF again.`,
        });
      }
      return;
    }
    w.focus();
    w.print();
  };

  const file = async () => {
    if (!tour || !fileIt) return;
    setFiling(true);
    setFileMessage(null);
    try {
      const r = await api.fileTourReport(tourId, tour.master_property_id);
      const on = r.filed_on ?? 'this property';
      setFileMessage({
        ok: true,
        text: !r.filed
          ? 'Nothing was filed.'
          : r.already_filed
            ? `This report was already in ${on}'s documents - nothing was written twice.`
            : `Filed on ${on} as a Walk-through report.`,
      });
    } catch (err) {
      setFileMessage({ ok: false, text: messageOf(err, 'The report could not be filed.') });
    } finally {
      setFiling(false);
    }
  };

  const promote = async (ids: string[]) => {
    if (!ids.length) return;
    setPromoting(true);
    setPromoteError(null);
    try {
      const r = await api.promoteTourPhotos(tourId, ids);
      if (!live.current) return;
      setResults((prev) => {
        const next = { ...prev };
        for (const res of r.results) next[res.photo_id] = res;
        return next;
      });
      // ⭐ I6: merged into the photos already shown. Fetching the tour again signs every link afresh, and the grid
      //   would download every full-size photo again - on a phone, after every Add. A photo is in the gallery
      //   exactly when its answer names the gallery row ('promoted' and 'already' do; a refusal never does).
      const landed = new Map<string, string>();
      for (const res of r.results) {
        if (res.property_photo_id) landed.set(res.photo_id, res.property_photo_id);
      }
      setTour((prev) =>
        prev
          ? { ...prev, photos: prev.photos.map((p) => (landed.has(p.id) ? { ...p, promoted_photo_id: landed.get(p.id) ?? null } : p)) }
          : prev,
      );
      setSelected(new Set());
    } catch (err) {
      if (live.current) setPromoteError(messageOf(err, 'The photos could not be added to the property.'));
    } finally {
      if (live.current) setPromoting(false);
    }
  };

  /**
   * I4 - the cover is his choice, and changeable (his answer, spec section 0). The tour keeps it and the report is
   * rebuilt so the frame shows it; until he chooses, the report's own rule stands - the first Exterior shot.
   */
  const chooseCover = async (photoId: string) => {
    setCoverBusy(true);
    setCoverRefusal(null);
    try {
      const r = await api.patchTour(tourId, { hero_photo_id: photoId });
      if (!live.current) return;
      setTour((prev) => (prev ? { ...prev, hero_photo_id: r.tour.hero_photo_id } : prev));
      await loadReport();
    } catch (err) {
      if (live.current) setCoverRefusal({ photoId, reason: messageOf(err, 'The cover could not be changed - try again.') });
    } finally {
      if (live.current) setCoverBusy(false);
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Layout>
      <div className="mx-auto max-w-5xl space-y-5">
        <div>
          <h1 className={H1}>Walk-through report</h1>
          {tour && (
            <p className="mt-1 text-[13px] text-gray-500">
              {where}
              {' · '}Toured by {tour.toured_by?.full_name ?? 'someone at Apex'}
              {' · '}
              {displayDate(tour.toured_at, DAY)}
            </p>
          )}
        </div>

        {missing > 0 && (
          <p role="note" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
            {missing === 1 ? '1 photo' : `${missing} photos`} not yet uploaded - the report says so on its cover. They
            appear here once the phone that took them has signal.
          </p>
        )}

        {reportError ? (
          <div className="space-y-2">
            <p role="alert" className="text-[13px] font-semibold text-red-700">
              {reportError}
            </p>
            <Button variant="outline" onClick={retryReport}>
              Try again
            </Button>
          </div>
        ) : report ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => void print()} isLoading={printing}>
                Print / PDF
              </Button>
              <Link to="/tours" className="text-[12.5px] font-semibold text-primary-500">
                All walk-throughs
              </Link>
            </div>
            {printMessage && (
              <p
                role={printMessage.ok ? 'status' : 'alert'}
                className={`text-[13px] ${printMessage.ok ? 'text-gray-800' : 'text-red-700'}`}
              >
                {printMessage.text}
              </p>
            )}
            <ScaledReport html={report.html} />
          </>
        ) : (
          <p className="text-[13px] text-gray-500">Building the report</p>
        )}

        {tour && (
          <section aria-label="Filing" className="space-y-2 rounded-xl border border-gray-200 bg-white p-4">
            <h2 className={H2}>Filing</h2>
            <label className="flex items-start gap-2 text-[13px]">
              <input type="checkbox" className="mt-1" checked={fileIt} onChange={(e) => setFileIt(e.target.checked)} />
              <span>
                File it on {where || 'this property'}
                <span className="block text-[12px] text-gray-600">
                  Off, nothing is written. On, the report is saved to this property&apos;s documents as a Walk-through
                  report, with its photos inside. Removing it later unlinks it; the file itself is kept.
                </span>
              </span>
            </label>
            <Button variant="outline" onClick={() => void file()} disabled={!fileIt || filing} isLoading={filing}>
              File the report
            </Button>
            {fileMessage && (
              <p role={fileMessage.ok ? 'status' : 'alert'} className={`text-[13px] ${fileMessage.ok ? 'text-gray-800' : 'text-red-700'}`}>
                {fileMessage.text}
              </p>
            )}
          </section>
        )}

        <section aria-label="Photos" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className={H2}>Photos</h2>
            <Button
              variant="outline"
              onClick={() => void promote([...selected])}
              disabled={selected.size === 0 || promoting}
              isLoading={promoting}
            >
              {`Add ${selected.size} selected to property photos`}
            </Button>
          </div>
          <p className="text-[12px] text-gray-600">
            Private to this walk-through until added. An added photo can appear in brochures and on the public listing;
            its note stays with the walk-through.
          </p>
          <p className="text-[12px] text-gray-600">The cover is the first Exterior shot unless you choose one.</p>
          {tourError && (
            <div className="space-y-2">
              <p role="alert" className="text-[13px] text-red-700">{tourError}</p>
              <Button size="sm" variant="outline" onClick={retryTour}>
                Try again
              </Button>
            </div>
          )}
          {promoteError && <p role="alert" className="text-[13px] text-red-700">{promoteError}</p>}
          {tour && tour.photos.length === 0 && <p className="text-[13px] text-gray-600">No photos have uploaded yet.</p>}
          {tour && tour.photos.length > 0 && (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {tour.photos.map((p, i) => {
                const added = Boolean(p.promoted_photo_id);
                const refused = results[p.id]?.status === 'refused' ? results[p.id] : null;
                const cover = tour.hero_photo_id === p.id;
                return (
                  <li key={p.id} aria-label={`Photo ${i + 1}`} className="space-y-1 rounded-lg border border-gray-200 bg-white p-2">
                    {p.url ? (
                      // Lazy: a 40-photo tour is ~25 MB of full-size photos, and a phone should fetch only what he scrolls to.
                      <img src={p.url} alt={tagLabel(p.subject)} loading="lazy" className="aspect-[4/3] w-full rounded object-cover" />
                    ) : (
                      <span className="flex aspect-[4/3] w-full items-center justify-center rounded bg-gray-100 text-[11.5px] text-gray-600">
                        The photo could not be loaded
                      </span>
                    )}
                    <span className="flex flex-wrap items-center gap-1.5 text-[12px] font-semibold text-gray-900">
                      {tagLabel(p.subject)}
                      {cover && (
                        <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[.04em] text-white">
                          Cover
                        </span>
                      )}
                    </span>
                    {p.note && <span className="block break-words text-[11.5px] text-gray-600">{p.note}</span>}
                    {added ? (
                      <span className="block text-[11.5px] font-semibold text-emerald-700">In property photos</span>
                    ) : (
                      <>
                        <label className="flex items-center gap-1.5 text-[12px] text-gray-700">
                          <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} disabled={promoting} />
                          Select
                        </label>
                        <Button size="sm" variant="outline" onClick={() => void promote([p.id])} disabled={promoting}>
                          Add to property photos
                        </Button>
                      </>
                    )}
                    {refused && (
                      <span className="block break-words text-[11.5px] text-red-700">Not added: {refused.reason}</span>
                    )}
                    {!cover && (
                      <Button size="sm" variant="ghost" onClick={() => void chooseCover(p.id)} disabled={coverBusy}>
                        Use as cover
                      </Button>
                    )}
                    {coverRefusal?.photoId === p.id && (
                      <span className="block break-words text-[11.5px] text-red-700">Not the cover: {coverRefusal.reason}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </Layout>
  );
}
